import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CalendarPlus, Loader2 } from "lucide-react";
import { fmtDate } from "./assessmentMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const today = () => new Date().toISOString().slice(0, 10);

export default function ScheduleVisitDialog({ item, onClose, onScheduled }) {
  const [rns, setRns] = useState([]);
  const [form, setForm] = useState({ scheduled_date: "", scheduled_time: "10:00", duration_minutes: 60, employee_id: "", notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!item) return;
    const due = item.due_date && item.due_date >= today() ? item.due_date : today();
    setForm((f) => ({ ...f, scheduled_date: due, employee_id: "" }));
    axios.get(`${API}/assessments/meta/rn-employees`).then((r) => {
      setRns(r.data.rns || []);
      if (r.data.me_employee_id) setForm((f) => ({ ...f, employee_id: r.data.me_employee_id }));
    }).catch(() => {});
  }, [item]);

  if (!item) return null;
  const label = (item.kind === "form_update" ? "supervisory" : item.kind) === "supervisory" ? "supervisory visit" : "re-admission assessment";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/assessments/schedule-visit`, {
        client_id: item.client_id, kind: (item.kind === "form_update" ? "supervisory" : item.kind), scheduled_date: form.scheduled_date, scheduled_time: form.scheduled_time,
        duration_minutes: Number(form.duration_minutes) || 60, employee_id: form.employee_id || null, notes: form.notes || null,
      });
      toast.success(`Visit booked for ${fmtDate(r.data.scheduled_date)}${r.data.employee_name ? ` with ${r.data.employee_name}` : ""}`);
      onScheduled?.(r.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not schedule visit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="schedule-visit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarPlus className="w-5 h-5 text-rose-600" /> Schedule {label}</DialogTitle>
          <DialogDescription>{item.client_name} · due {fmtDate(item.due_date)}. The visit is added to the Schedule calendar.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <Label className="text-xs">Date</Label>
              <Input type="date" value={form.scheduled_date} onChange={(e) => set("scheduled_date", e.target.value)} data-testid="sv-date" />
            </div>
            <div>
              <Label className="text-xs">Time</Label>
              <Input type="time" value={form.scheduled_time} onChange={(e) => set("scheduled_time", e.target.value)} data-testid="sv-time" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Duration (min)</Label>
              <Input type="number" min={15} step={15} value={form.duration_minutes} onChange={(e) => set("duration_minutes", e.target.value)} data-testid="sv-duration" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">RN</Label>
              <Select value={form.employee_id || "none"} onValueChange={(v) => set("employee_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="sv-rn-select"><SelectValue placeholder="Pick an RN" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {rns.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Note (optional)</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Gate code, preferred time, who to call…" data-testid="sv-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !form.scheduled_date} className="gap-1 bg-rose-600 hover:bg-rose-700" data-testid="sv-save-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarPlus className="w-4 h-4" />} Book visit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
